/* ============================================================
   Tool registry — the single source of truth for routes and cards.

   Audit A-22: the homepage advertised ten tools and four existed; six
   navigations dead-ended. Routes and cards are now generated from this
   one list, so a tool cannot appear on the homepage without a route,
   and a `planned` tool renders an honest roadmap state rather than a
   broken screen.
   ============================================================ */

export const TRACKS = {
  move: {
    id: 'move',
    label: 'Move',
    caption: 'Operational diagnostics',
    blurb: 'For operations already running. Find the cost, the leak and the deadline.'
  },
  build: {
    id: 'build',
    label: 'Build',
    caption: 'Greenfield navigation',
    blurb: 'For operations being set up. Licensing, structure, people and place.'
  }
};

export const TOOLS = [
  /* ---------------- Move ---------------- */
  {
    id: 'scorecard',
    description:
      "Free 32-question corridor health diagnostic across 8 weighted domains for Kenyan and East African freight operators. Weighted index, priority findings and a board-ready PDF, computed in your browser.",
    path: '/scorecard',
    track: 'move',
    status: 'live',
    title: 'Corridor Health Scorecard',
    summary: '8 domains, 32 questions, a weighted index and a board-ready export.',
    icon: 'chart',
    accent: 'var(--ca-chart-1)'
  },
  {
    id: 'benchmark',
    description:
      "Calculate your true cost per tonne-kilometre on Kenyan corridors and compare it against sourced benchmarks \u2014 Mombasa to Nairobi, Naivasha, Kampala and Kigali, road and SGR rail.",
    path: '/benchmark',
    track: 'move',
    status: 'live',
    title: 'Cost-per-Ton-Km Calculator',
    summary: 'Your true corridor cost against sourced Kenyan benchmarks.',
    icon: 'truck',
    accent: 'var(--ca-chart-2)'
  },
  {
    id: 'compliance',
    description:
      "Every Kenyan freight regulatory instrument as a dated obligation \u2014 KRA Advance Cargo Declaration, NTSA CVRL and CVOL, KPA Tariff, IDF and RDL \u2014 with status computed from today.",
    path: '/compliance',
    track: 'move',
    status: 'live',
    title: 'Compliance Calendar',
    summary: 'Every instrument as a dated obligation, with live status and renewals.',
    icon: 'calendar',
    accent: 'var(--ca-chart-3)'
  },
  {
    id: 'glossary',
    description:
      "Twenty Kenyan freight and customs terms explained: ACD, AEO, CB6, demurrage, detention, IDF, RDL, PVoC, SGR and more, each tied to the instrument behind it.",
    path: '/glossary',
    track: 'move',
    status: 'live',
    title: 'Regulatory Glossary',
    summary: 'Twenty terms, each tied to the instrument behind it.',
    icon: 'document',
    accent: 'var(--ca-chart-5)'
  },
  {
    id: 'landed-cost',
    description:
      "Full landed cost for a Kenyan import: CIF, EAC Common External Tariff duty, excise, VAT, IDF at 2.5 per cent and RDL at 2 per cent, plus a tiered demurrage countdown.",
    path: '/landed-cost',
    track: 'move',
    status: 'live',
    title: 'Landed Cost & Demurrage Guard',
    summary: 'Full landed cost including IDF, RDL, duty and VAT, with a free-time countdown.',
    icon: 'coins',
    accent: 'var(--ca-chart-4)'
  },
  {
    id: 'readiness',
    description:
      "The 48-hour data readiness checklist a logistics diagnostic needs before it can start, with blocking items separated from the merely useful.",
    path: '/readiness',
    track: 'move',
    status: 'live',
    title: '48-Hour Data Readiness',
    summary: 'Pre-diagnostic checklist for a deployed engagement.',
    icon: 'clock',
    accent: 'var(--ca-chart-6)'
  },
  {
    id: 'profitability',
    description:
      "Estimate route profitability for African road freight: contribution per trip, break-even volume, break-even rate, and what the empty return leg is costing you.",
    path: '/profitability',
    track: 'move',
    status: 'live',
    title: 'Route Profitability Estimator',
    summary: 'Revenue against cost per route, with break-even.',
    icon: 'data',
    accent: 'var(--ca-chart-7)'
  },
  {
    id: 'emissions',
    description:
      "Calculate freight emissions in grams of CO2e per tonne-kilometre on the GLEC Framework and ISO 14083, with the data-quality level stated \u2014 the figure shippers now ask for.",
    path: '/emissions',
    track: 'move',
    status: 'live',
    title: 'Corridor Emissions (ISO 14083)',
    summary: 'gCO₂e per tonne-km on the GLEC method, for shipper tenders.',
    icon: 'leaf',
    accent: 'var(--ca-chart-2)'
  },

  {
    id: 'corridor-run',
    description:
      "One consignment, every consequence: cost per tonne-km against the published band, margin on the trip, import duty payable on arrival, demurrage accruing at the terminal, ISO 14083 carbon for the tender, and what the duty float does to your cash \u2014 computed together, in your browser.",
    path: '/corridor-run',
    track: 'move',
    status: 'live',
    title: 'The Corridor Run',
    summary:
      'Cost, margin, duty, demurrage, carbon and cash for one consignment \u2014 and what they do to each other.',
    icon: 'truck',
    accent: 'var(--ca-chart-6)'
  },
  {
    id: 'financeability',
    description:
      "See what a lender sees: cash conversion cycle including duty float, working capital gap, debt service coverage, and the one dimension holding your file back.",
    path: '/financeability',
    track: 'move',
    status: 'live',
    title: 'Financeability Pack',
    summary: 'Cash cycle, working capital gap and DSCR — what a lender sees before they say so.',
    icon: 'coins',
    accent: 'var(--ca-chart-1)'
  },
  {
    id: 'ntb',
    description:
      "Log a non-tariff barrier where it happens \u2014 weighbridge, border post, roadblock \u2014 and get a formatted submission for the AfCFTA mechanism at tradebarriers.africa.",
    path: '/ntb',
    track: 'move',
    status: 'live',
    title: 'NTB Reporter',
    summary: 'Log a non-tariff barrier where it happens, then lodge it at tradebarriers.africa.',
    icon: 'alert',
    accent: 'var(--ca-chart-8)'
  },
  {
    id: 'compare',
    description:
      'Two saved corridor runs side by side — what moved, by how much, and what makes the two not directly comparable.',
    path: '/compare',
    track: 'move',
    status: 'live',
    title: 'Compare runs',
    summary: 'Two saved runs side by side, with the caveats that make them readable.',
    icon: 'chart',
    accent: 'var(--ca-chart-3)'
  },
  {
    id: 'saved',
    description:
      "Everything Kanda has stored on your device. Review it, export it as JSON, or erase it permanently.",
    path: '/saved',
    track: 'move',
    status: 'live',
    title: 'Saved work',
    summary: 'Everything stored on this device — review it, export it, or erase it.',
    icon: 'lock',
    accent: 'var(--ca-chart-7)'
  },

  /* ---------------- Build ---------------- */
  {
    id: 'licensing',
    description:
      "Step-by-step licensing pathways for Kenyan road freight operators, customs clearing agents and bonded warehouse operators, covering NTSA, KRA, KPA and county requirements.",
    path: '/licensing',
    track: 'build',
    status: 'live',
    title: 'Licensing Navigator',
    summary: 'NTSA, KRA, KPA and EAC — phase by phase, every step cited.',
    icon: 'pin',
    accent: 'var(--ca-chart-3)'
  },
  {
    id: 'implementation',
    description:
      "Plan a Kenyan logistics setup by gate rather than by date: five dependency-ordered gates across BRS, KRA, NTSA, county and customs, with the capital each releases and the exposure created by committing ahead of one.",
    path: '/implementation',
    track: 'build',
    status: 'live',
    title: 'Implementation Planner',
    summary: 'Five gates, six agencies, and the capital you have committed ahead of them.',
    icon: 'clock',
    accent: 'var(--ca-chart-4)'
  },
  {
    id: 'organogram',
    description:
      "Required and recommended personnel for Kenyan logistics operations, with the regulatory reason for each post: road freight, warehouse and ICD, customs clearing.",
    path: '/organogram',
    track: 'build',
    status: 'live',
    title: 'Postholder Planner',
    summary: 'Required and recommended personnel by operation type.',
    icon: 'group',
    accent: 'var(--ca-chart-4)'
  },
  {
    id: 'sez',
    description:
      "Compare Kenyan Special Economic Zones and Export Processing Zones for a logistics or manufacturing investment: Dongo Kundu, Tatu City, Naivasha ICD and Athi River.",
    path: '/sez',
    track: 'build',
    status: 'live',
    title: 'SEZ / EPZ Positioning Guide',
    summary: 'Evaluate special economic zones against your investment.',
    icon: 'warehouse',
    accent: 'var(--ca-chart-6)'
  },
  {
    id: 'capital',
    description:
      "Model debt, equity and grant layers for an African logistics venture, with debt service coverage.",
    path: '/capital',
    track: 'build',
    status: 'live',
    title: 'Capital-Stack Builder',
    summary: 'Model debt, equity and grant layers with DSCR.',
    icon: 'coins',
    accent: 'var(--ca-chart-1)'
  },
  {
    id: 'structure',
    description:
      "Holding and operating company structures for cross-border African logistics ventures.",
    path: '/structure',
    track: 'build',
    status: 'live',
    title: 'Corporate Structure Designer',
    summary: 'Holding and operating company templates across jurisdictions.',
    icon: 'setting',
    accent: 'var(--ca-chart-7)'
  }
];

export function toolsInTrack(trackId) {
  return TOOLS.filter((t) => t.track === trackId);
}

export function getTool(id) {
  return TOOLS.find((t) => t.id === id) || null;
}

export function getToolByPath(path) {
  return TOOLS.find((t) => t.path === path) || null;
}

export function liveTools() {
  return TOOLS.filter((t) => t.status === 'live');
}

export function plannedTools() {
  return TOOLS.filter((t) => t.status === 'planned');
}
